import {
  Button,
  Container,
  ListItemsWrapper,
  useClickOutside,
} from "@fluidattacks/design";
import { isEmpty } from "lodash";
import { Fragment, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddAction } from "./add-action";
import { EditAction } from "./edit-button";
import { RemoveAction } from "./remove-action";

import type { IStakeholderDataSet } from "../../types";
import { useAuthz } from "hooks/use-authz";

interface IActionsButtonsProps {
  organizationId: string;
  loading: boolean;
  resetOnAction: VoidFunction;
  selectedMembers: IStakeholderDataSet[];
  allMembers: IStakeholderDataSet[];
}

const OrgActionButtons = ({
  resetOnAction,
  organizationId,
  selectedMembers,
  loading,
  allMembers,
}: IActionsButtonsProps): JSX.Element => {
  const { canMutate } = useAuthz();
  const { t } = useTranslation();
  const canAdd = canMutate("grant_stakeholder_organization_access");
  const canEdit = canMutate("update_organization_stakeholder");
  const canRemove = canMutate("remove_stakeholder_organization_access");
  const areButtonsDisabled = isEmpty(selectedMembers) || loading;
  const areThereMultipleSelections = selectedMembers.length > 1;

  const additionalRef = useRef<HTMLDivElement>(null);
  const [openActions, setOpenActions] = useState(false);

  const handleDropdown = useCallback((): void => {
    setOpenActions((previousState): boolean => !previousState);
  }, [setOpenActions]);

  const onReset = useCallback((): void => {
    setOpenActions(false);
    resetOnAction();
  }, [resetOnAction, setOpenActions]);

  useClickOutside(
    additionalRef.current,
    (): void => {
      setOpenActions(false);
    },
    true,
  );

  return (
    <Fragment>
      {(canAdd || canEdit || canRemove) && (
        <Button
          data-testid={"org-members-actions-button"}
          disabled={areButtonsDisabled}
          icon={"ellipsis-vertical"}
          id={"org-members-actions-button"}
          onClick={handleDropdown}
          tooltip={
            areButtonsDisabled
              ? t("tables.members.buttons.manage.tooltipDisabled")
              : ""
          }
          variant={"ghost"}
        >
          {t("tables.members.buttons.manage.text")}
        </Button>
      )}
      <Container
        display={openActions ? "block" : "none"}
        maxWidth={"100px"}
        mt={0.25}
        position={"absolute"}
        right={"220px"}
        top={"35px"}
        zIndex={2}
      >
        <ListItemsWrapper role={"menu"} tabIndex={-1}>
          {canEdit && (
            <EditAction
              allMembers={allMembers}
              disabled={areButtonsDisabled || areThereMultipleSelections}
              organizationId={organizationId}
              resetOnAction={onReset}
              selectedMembers={selectedMembers}
            />
          )}
          {canRemove && (
            <RemoveAction
              disabled={areButtonsDisabled}
              organizationId={organizationId}
              resetOnAction={onReset}
              selectedMembers={selectedMembers}
            />
          )}
        </ListItemsWrapper>
      </Container>
      {canAdd && (
        <AddAction
          allMembers={allMembers}
          organizationId={organizationId}
          resetOnAction={onReset}
          selectedMembers={selectedMembers}
        />
      )}
    </Fragment>
  );
};

export { OrgActionButtons };
export type { IActionsButtonsProps };
